import { atom, type PrimitiveAtom } from 'jotai';
import { atomFamily } from 'jotai/utils';
import type { BranchCode, CallSchedule, EditingJobNumberItem, JobNumberItem, ScheduleState, TimeRange } from '../types';

export const createEditingJobNumberItem = (): EditingJobNumberItem => ({
  id: crypto.randomUUID(),
  jobNumber: '',
  status: 'editing',
});

// MOCK [#239]: 件数が多いケース（2本部東京は実運用で約459件）の検索・ページネーション動作を
// 確認できるよう、ダミーの仕事Noを大量に生成する。
const mockTokyoJobNumbers: readonly string[] = [
  'lwc11',
  'tki0520aa',
  'tki0516aa-01',
  ...Array.from({ length: 456 }, (_, i) => `tki${String(1000 + i).padStart(4, '0')}aa`),
];

// MOCK [#239]: 本番実装時は本関数と初期値利用箇所を削除し、バックエンドから取得した仕事Noリストで atom を初期化する。
const buildDefaultJobNumbers = (code: string): JobNumberItem[] => {
  const seedByCode: Record<string, readonly string[]> = {
    バイトル東京モバイル: ['tky0105aa'],
    バイトル東京オフィス: ['tky0405aa'],
    airwork東京モバイル: ['tky0105aa'],
    '東京/オフィス/airwork': ['tky0405aa'],
    '大阪/モバイル/バイトルドットコム': ['osk0118aa'],
    '大阪/オフィス/バイトルドットコム': ['osk0418aa'],
    '東京/営業2本部/バイトル': mockTokyoJobNumbers,
    '東京/営業2本部/AirWork(ﾄﾘﾌﾟﾙ2)': ['lwhn4', 'lwhn26', 'lwin1', 'lwcf2', 'lwcf3', 'lwfw2'],
    '大阪/営業2本部/バイトルドットコム': [
      'lwe4',
      'lwa95',
      'lwa29',
      'lwe8',
      'lwr1',
      'lwr5',
      'lwa130',
      'lwa197',
      'lwr3',
      'lwax1',
      'lwa208',
      'lwab6',
      'lwbj1',
      'lwr21',
      'lwg4',
      'lwfb1',
      'lwa204',
      'lwcs3',
      'lwi5',
      'lwiz1',
      'lwid1',
      'lwar8',
      'lwhn20',
    ],
  };
  return (seedByCode[code] ?? []).map(jobNumber => ({
    id: crypto.randomUUID(),
    jobNumber,
    status: 'committed',
  }));
};

// MOCK [#239]: 本番実装時は本関数と初期値利用箇所を削除し、バックエンドから取得した稼働時間設定で atom を初期化する。
const buildDefaultSchedule = (branchCode: BranchCode): CallSchedule => {
  const weekdayDefaultByBranch: Record<BranchCode, TimeRange> = {
    '1honbu-tokyo': { from: '10:00', to: '17:00' },
    '1honbu-osaka': { from: '10:00', to: '17:00' },
    '2honbu-tokyo': { from: '09:00', to: '18:15' },
    '2honbu-osaka': { from: '09:00', to: '18:15' },
  };
  const weekdayDefault = weekdayDefaultByBranch[branchCode];
  const weekendDefault: TimeRange = { from: null, to: null };
  return {
    weekdays: {
      mon: { ...weekdayDefault },
      tue: { ...weekdayDefault },
      wed: { ...weekdayDefault },
      thu: { ...weekdayDefault },
      fri: { ...weekdayDefault },
      sat: { ...weekendDefault },
      sun: { ...weekendDefault },
    },
    // 現状のハードコード挙動（祝日・年末年始は架電しない）に合わせた初期値
    callOnHoliday: false,
    callOnYearEnd: false,
  };
};

// atomFamily はキーを値比較でキャッシュ区別するため、末尾スペース等で揺れた code が来ると別 atom に分裂する。
// 現状キーは BRANCHES_BY_HONBU の定数リテラル由来で揺れないが、#239 のバックエンド連携で
// 実データを渡す際は、境界で正規化するファクトリ（trim 等）+ ブランド型に code を通して分裂を防ぐこと。
export const jobNumbersAtomFamily = atomFamily((code: string) => atom<JobNumberItem[]>(buildDefaultJobNumbers(code)));

// 拠点をキーとする atom はキーがリテラルユニオンのため、上記の分裂は起こり得ない。
export const scheduleAtomFamily = atomFamily((branchCode: BranchCode) =>
  atom<ScheduleState>({
    current: buildDefaultSchedule(branchCode),
    baseline: buildDefaultSchedule(branchCode),
  })
);

// 架電の転送先を東京拠点に切り替えているか。稼働時間と違い運用中に即時反映したい単発の切替のため、
// 保存ボタンを介さず current/baseline も持たない。
export const forwardToTokyoAtomFamily = atomFamily<BranchCode, PrimitiveAtom<boolean>>(() => atom(false));
